/**
 * LineChart — minimal multi-series SVG line chart with axis labels.
 *
 * Why hand-rolled, not recharts: we render ONE chart on the reports
 * page; pulling 50 KB of dependency to draw a polyline is the wrong
 * trade-off for an MVP admin tool. The chart auto-scales to its
 * container width via a viewBox.
 *
 * The component is pure — no state, no effects. Pass in the data
 * and the parent decides when to re-render.
 */
import { useMemo } from "react";

export interface LineSeries {
  readonly label: string;
  readonly colour: string;
  /** Same length as `xLabels`; values are numeric. */
  readonly values: readonly number[];
}

interface Props {
  readonly xLabels: readonly string[];
  readonly series: readonly LineSeries[];
  /** Y-axis number formatter (e.g. compact currency). */
  readonly formatY?: (v: number) => string;
  readonly heightPx?: number;
}

const PAD = { top: 12, right: 12, bottom: 28, left: 56 } as const;
const VIEW_W = 600;

export function LineChart({
  xLabels,
  series,
  formatY = (v) => v.toLocaleString(),
  heightPx = 200,
}: Props): JSX.Element {
  const view = useMemo(() => {
    const yMax = Math.max(1, ...series.flatMap((s) => s.values));
    const xCount = Math.max(1, xLabels.length);
    const innerW = VIEW_W - PAD.left - PAD.right;
    const innerH = heightPx - PAD.top - PAD.bottom;
    const xStep = xCount > 1 ? innerW / (xCount - 1) : innerW;
    const pointX = (i: number): number => PAD.left + i * xStep;
    const pointY = (v: number): number => PAD.top + innerH - (v / yMax) * innerH;
    const polylines = series.map((s) => ({
      colour: s.colour,
      label: s.label,
      d: s.values.map((v, i) => `${pointX(i)},${pointY(v)}`).join(" "),
    }));
    // Y gridlines + labels — 4 ticks (0, 33%, 66%, 100% of max).
    const yTicks = [0, 0.33, 0.66, 1].map((p) => ({
      y: PAD.top + innerH - p * innerH,
      label: formatY(yMax * p),
    }));
    // X gridlines — show ~5 ticks for readability.
    const xTickEvery = Math.max(1, Math.ceil(xCount / 5));
    const xTicks = xLabels
      .map((label, i) => ({ x: pointX(i), label, i }))
      .filter((t) => t.i % xTickEvery === 0 || t.i === xLabels.length - 1);
    return { polylines, yTicks, xTicks };
  }, [xLabels, series, heightPx, formatY]);

  if (xLabels.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-simuflex-slate"
        style={{ height: heightPx }}
      >
        No data in this window.
      </div>
    );
  }

  return (
    <div>
      <svg
        viewBox={`0 0 ${VIEW_W} ${heightPx}`}
        className="w-full"
        preserveAspectRatio="none"
        style={{ height: heightPx }}
        role="img"
        aria-label="Trend chart"
      >
        {/* Y gridlines + labels */}
        {view.yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              x2={VIEW_W - PAD.right}
              y1={t.y}
              y2={t.y}
              stroke="#E1E6EC"
              strokeWidth={1}
              strokeDasharray={i === 0 ? "" : "2 3"}
            />
            <text x={PAD.left - 6} y={t.y + 3} textAnchor="end" fontSize={10} fill="#6C757D">
              {t.label}
            </text>
          </g>
        ))}
        {/* X labels */}
        {view.xTicks.map((t) => (
          <text key={t.i} x={t.x} y={heightPx - 8} textAnchor="middle" fontSize={10} fill="#6C757D">
            {t.label}
          </text>
        ))}
        {/* Series polylines */}
        {view.polylines.map((p, i) => (
          <polyline
            key={i}
            fill="none"
            stroke={p.colour}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={p.d}
          />
        ))}
      </svg>
      {/* Legend */}
      <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-simuflex-slate">
        {series.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-1.5 rounded-sm"
              style={{ backgroundColor: s.colour }}
            />
            <span className="font-semibold">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
